import { Link } from 'react-router-dom';
import { useEffect, useState, useCallback, useRef } from 'react';
import { CheckCircle, Star, Phone, MapPin, ArrowRight, Heart, Shield, Clock, DollarSign, Accessibility, Users, ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { getTestimonials, getServiceAreas } from '../services/api';
import SEO, { schemas } from '../components/SEO';

// ── Photo gallery — all 16 real fleet photos ─────────────────────────────────
const GALLERY = [
  { src: '/images/fleet-lineup-wide.jpg',     alt: 'Full MDS fleet lineup',                   span: 'col-span-2' },
  { src: '/images/van-ramp-navy.jpg',         alt: 'Wheelchair ramp on navy van',              span: 'col-span-1' },
  { src: '/images/van-ramp-silver-back.jpg',  alt: 'Silver van rear ramp deployed',            span: 'col-span-1' },
  { src: '/images/van-interior-wide.jpg',     alt: 'Accessible van interior',                  span: 'col-span-1' },
  { src: '/images/fleet-4cars.jpg',           alt: 'Four MDS branded vehicles',                span: 'col-span-1' },
  { src: '/images/van-interior-cargo.jpg',    alt: 'Van interior with wheelchair tie-downs',   span: 'col-span-1' },
  { src: '/images/van-rear-ramps-open.jpg',   alt: 'Three vans with ramps deployed',           span: 'col-span-2' },
  { src: '/images/fleet-silver-branded.jpg',  alt: 'Silver branded fleet',                     span: 'col-span-1' },
  { src: '/images/fleet-parking-lot.jpg',     alt: 'MDS silver Sienna',                        span: 'col-span-1' },
  { src: '/images/sienna-front-two.jpg',      alt: 'Two MDS Toyota Siennas',                   span: 'col-span-1' },
  { src: '/images/fleet-3siennas.jpg',        alt: 'Three Sienna fleet row',                   span: 'col-span-1' },
  { src: '/images/fleet-silver-row.jpg',      alt: 'Silver fleet row with branding',           span: 'col-span-1' },
  { src: '/images/fleet-row-side.jpg',        alt: 'Fleet rear row view',                      span: 'col-span-1' },
  { src: '/images/sienna-parking.jpg',        alt: 'MDS Sienna in parking lot',                span: 'col-span-1' },
  { src: '/images/van-interior-ramp2.jpg',    alt: 'Van accessible interior with ramp',        span: 'col-span-1' },
  { src: '/images/fleet-mixed-lot.jpg',       alt: 'Mixed fleet white and dark vehicles',      span: 'col-span-1' },
];

const SERVICES = [
  { icon: '🚑', title: 'Non-Emergency Medical Transport', desc: 'Door-to-door rides to doctor appointments, dialysis, therapy sessions, and specialist visits.' },
  { icon: '♿', title: 'Wheelchair-Accessible Transport',  desc: 'ADA-compliant vehicles with ramps and lifts. Trained drivers for boarding and disembarking assistance.' },
  { icon: '👴', title: 'Senior Assisted Transportation',   desc: 'Specialized rides for seniors to medical check-ups, grocery stores, religious services, and social events.' },
  { icon: '🏥', title: 'Hospital Discharge Transport',     desc: 'Safe, comfortable transfers from hospital to home or care facility after surgery or illness.' },
  { icon: '💙', title: 'Special Needs Transport',          desc: 'Patient-centered services for individuals with chronic illness, disabilities, or specialized healthcare needs.' },
  { icon: '🔁', title: 'Recurring Transportation',         desc: 'Weekly or monthly transport plans with personalized scheduling for ongoing treatments.' },
];

const WHY = [
  { icon: Shield,       title: 'Fully Licensed & Insured',  desc: 'All drivers are licensed, trained, and insured for your complete peace of mind.' },
  { icon: Clock,        title: 'Timely & Reliable',          desc: 'Punctual pick-ups and drop-offs — we understand that medical appointments cannot wait.' },
  { icon: Heart,        title: 'Compassionate Care',         desc: 'We treat every passenger with dignity, patience, and genuine kindness.' },
  { icon: DollarSign,   title: 'Transparent Pricing',        desc: 'No hidden fees. Affordable rates with clear, upfront pricing you can trust.' },
  { icon: Accessibility,title: 'Fully Accessible',           desc: 'Wheelchair ramps, lifts, and secure tie-down systems for maximum accessibility.' },
  { icon: Users,        title: 'Experienced Team',           desc: 'Professional drivers trained in patient care, first aid, and mobility assistance.' },
];

// ── Video Hero Section ────────────────────────────────────────────────────────
function VideoHero() {
  const videoRef = useRef(null);
  const [muted, setMuted]     = useState(true);
  const [playing, setPlaying] = useState(true);

  const toggleMute = () => {
    if (videoRef.current) { videoRef.current.muted = !muted; setMuted(m => !m); }
  };
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (playing) { videoRef.current.pause(); setPlaying(false); }
    else         { videoRef.current.play();  setPlaying(true);  }
  };

  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden">
      {/* Video background */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover z-0"
        src="/videos/hero-fleet.mp4"
        poster="/images/poster-hero.jpg"
        autoPlay
        loop
        muted
        playsInline
      />
      {/* Dark + purple gradient overlay */}
      <div className="absolute inset-0 z-0 bg-gradient-to-r from-gray-900/92 via-purple-900/75 to-gray-900/40" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 bg-purple-700/30 border border-purple-400/40 text-purple-200 text-sm font-medium px-4 py-1.5 rounded-full mb-6 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            Non-Emergency Medical Transportation
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6 font-heading">
            Reliable Rides,<br />
            <span className="text-purple-300">Compassionate</span><br />
            Care
          </h1>
          <p className="text-gray-200 text-lg leading-relaxed mb-8">
            Safe, dependable transportation for seniors, patients, and individuals with mobility challenges across Fort Wayne, Indianapolis and nearby counties.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/book" className="btn-primary flex items-center gap-2 text-base">
              Book a Ride Now <ArrowRight size={18} />
            </Link>
            <Link to="/services" className="btn-outline border-white text-white hover:bg-white hover:text-purple-800 flex items-center gap-2 text-base">
              Our Services
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap gap-6 text-sm text-gray-300">
            {['Licensed & Insured', 'Wheelchair Accessible', 'On-Time Guarantee'].map(tag => (
              <span key={tag} className="flex items-center gap-1.5">
                <CheckCircle size={15} className="text-green-400" />{tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Video controls — bottom right */}
      <div className="absolute bottom-6 right-6 z-20 flex items-center gap-2">
        <button
          onClick={togglePlay}
          className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/40 flex items-center justify-center text-white transition-all"
          title={playing ? 'Pause video' : 'Play video'}
        >
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <button
          onClick={toggleMute}
          className="w-9 h-9 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/40 flex items-center justify-center text-white transition-all"
          title={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1 text-white/50 text-xs animate-bounce">
        <div className="w-5 h-8 border-2 border-white/30 rounded-full flex items-start justify-center pt-1.5">
          <div className="w-1 h-2 bg-white/60 rounded-full" />
        </div>
      </div>
    </section>
  );
}

// ── Gallery with lightbox ─────────────────────────────────────────────────────
function FleetGallery() {
  const [lightbox, setLightbox] = useState(null); // index of open image

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-purple-700 font-semibold text-sm uppercase tracking-wider">Our Fleet</span>
          <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-4 font-heading">See Our Vehicles in Action</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Our modern, fully equipped fleet is purpose-built for non-emergency medical transportation — wheelchair accessible, clean, and comfortable.
          </p>
        </div>

        {/* Masonry-style grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 auto-rows-[180px]">
          {GALLERY.map((img, i) => (
            <div
              key={i}
              className={`relative overflow-hidden rounded-xl cursor-pointer group ${img.span} ${
                i === 0 ? 'row-span-2' : i === 7 ? 'row-span-2' : ''
              }`}
              onClick={() => setLightbox(i)}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-purple-900/0 group-hover:bg-purple-900/40 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                  <Play size={18} className="text-purple-700 ml-0.5" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Lightbox */}
        {lightbox !== null && (
          <div
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <button
              className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl font-light w-10 h-10 flex items-center justify-center"
              onClick={() => setLightbox(null)}
            >✕</button>
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white"
              onClick={e => { e.stopPropagation(); setLightbox(l => (l - 1 + GALLERY.length) % GALLERY.length); }}
            ><ChevronLeft size={20} /></button>
            <img
              src={GALLERY[lightbox].src}
              alt={GALLERY[lightbox].alt}
              className="max-h-[85vh] max-w-[90vw] rounded-xl object-contain shadow-2xl"
              onClick={e => e.stopPropagation()}
            />
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white"
              onClick={e => { e.stopPropagation(); setLightbox(l => (l + 1) % GALLERY.length); }}
            ><ChevronRight size={20} /></button>
            <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-sm">
              {lightbox + 1} / {GALLERY.length} — {GALLERY[lightbox].alt}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

// ── Video showcase (2 short clips side by side) ───────────────────────────────
function VideoShowcase() {
  return (
    <section className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">Accessibility in Action</span>
          <h2 className="text-4xl font-bold text-white mt-2 mb-4 font-heading">Watch Our Ramp & Lift System</h2>
          <p className="text-gray-400 max-w-xl mx-auto">
            Our vehicles are equipped with industry-leading BraunAbility ramps and lift systems, making boarding safe and effortless for every passenger.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Video 2 */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            <video
              className="w-full aspect-video object-cover"
              src="/videos/fleet-action.mp4"
              poster="/images/poster-action.jpg"
              controls
              playsInline
              preload="metadata"
            />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-4 pointer-events-none">
              <p className="text-white font-semibold text-sm">♿ Ramp Deployment Demo</p>
              <p className="text-white/60 text-xs">See how quickly and smoothly our ramp deploys</p>
            </div>
          </div>

          {/* Video 3 */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            <video
              className="w-full aspect-video object-cover"
              src="/videos/fleet-service.mp4"
              poster="/images/poster-service.jpg"
              controls
              playsInline
              preload="metadata"
            />
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-4 pointer-events-none">
              <p className="text-white font-semibold text-sm">🚐 Fleet Service Walkthrough</p>
              <p className="text-white/60 text-xs">Spacious, clean, and fully equipped interiors</p>
            </div>
          </div>
        </div>

        {/* ADA badge row */}
        <div className="mt-10 flex flex-wrap justify-center gap-6">
          {[
            { emoji: '♿', text: 'BraunAbility Ramps' },
            { emoji: '🔒', text: 'Tie-Down Safety Systems' },
            { emoji: '🧯', text: 'Fire Extinguisher Onboard' },
            { emoji: '🛡️', text: 'ADA Compliant Fleet' },
          ].map(({ emoji, text }) => (
            <div key={text} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-5 py-2.5 text-white/80 text-sm">
              <span className="text-lg">{emoji}</span> {text}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);
  const [areas, setAreas]               = useState([]);

  useEffect(() => {
    getTestimonials().then(r => setTestimonials(r.data.testimonials)).catch(() => {});
    getServiceAreas().then(r => setAreas(r.data.areas)).catch(() => {});
  }, []);

  return (
    <div className="overflow-hidden">
      <SEO
        title="Reliable Non-Emergency Medical Transportation in Indianapolis"
        description="MDS Transportation LLC provides safe, accessible NEMT in Fort Wayne, Indianapolis and nearby counties. Wheelchair vehicles, senior transport, hospital discharge rides. Book online or call +1 (317) 982-1959."
        path="/"
        schema={testimonials.length ? schemas.reviews(testimonials) : schemas.home}
      />

      {/* ── 1. Video Hero ──────────────────────────────────────────────── */}
      <VideoHero />

      {/* ── 2. Stats bar ───────────────────────────────────────────────── */}
      <section className="bg-purple-700 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            {[['500+','Rides Completed'],['5★','Average Rating'],['24/7','Availability'],['2+','Service Areas']].map(([val, label]) => (
              <div key={label}>
                <div className="text-3xl font-bold font-heading">{val}</div>
                <div className="text-purple-200 text-sm mt-1">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. Who we serve ────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-purple-700 font-semibold text-sm uppercase tracking-wider">Who We Serve</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-6 font-heading">Transportation built around <em>your needs</em></h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                At MDS Transportation LLC, we believe that mobility is essential to well-being. We specialize in delivering safe, comfortable, and reliable transportation for individuals who require extra care and assistance.
              </p>
              <div className="space-y-3">
                {[
                  'Hospital discharge',
                  'Senior Individuals needing assisted transportation',
                  'Individuals recovering from surgery',
                  'People with mobility challenges requiring wheelchair-accessible transport',
                  'Patients requiring recurring dialysis, therapy, or specialist visits',
                ].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-purple-700 mt-0.5 shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/about" className="btn-primary inline-flex items-center gap-2 mt-8">
                Learn More About Us <ArrowRight size={16} />
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img src="/images/van-rear-ramps-open.jpg" alt="MDS Transportation wheelchair accessible vehicles" className="w-full h-80 object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-2xl">✅</div>
                  <div>
                    <p className="font-bold text-gray-900">Fully Certified</p>
                    <p className="text-sm text-gray-500">Licensed, insured & trained</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. Services ────────────────────────────────────────────────── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-purple-700 font-semibold text-sm uppercase tracking-wider">What We Offer</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-2 mb-4 font-heading">Our Transportation Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Comprehensive non-emergency medical transportation solutions tailored to your specific needs.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map(s => (
              <div key={s.title} className="card hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-100">
                <div className="text-4xl mb-4">{s.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 font-heading">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services" className="btn-outline inline-flex items-center gap-2">View All Services <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* ── 5. Fleet Photo Gallery ─────────────────────────────────────── */}
      <FleetGallery />

      {/* ── 6. Video showcase ──────────────────────────────────────────── */}
      <VideoShowcase />

      {/* ── 7. Why choose us ───────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-purple-700 font-semibold text-sm uppercase tracking-wider">Why MDS</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-2 font-heading">Why Choose MDS Transportation?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {WHY.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                  <Icon size={22} className="text-purple-700" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 8. Testimonials ────────────────────────────────────────────── */}
      <section className="py-20 bg-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-purple-200 font-semibold text-sm uppercase tracking-wider">Reviews</span>
            <h2 className="text-4xl font-bold text-white mt-2 font-heading">What Our Customers Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(testimonials.length ? testimonials.slice(0, 3) : [
              { id: 1, author_name: 'Sarah J.',  location: 'Indianapolis',    content: 'MDS Transportation made a huge difference in my recovery journey. Their drivers were kind, patient, and professional.', rating: 5 },
              { id: 2, author_name: 'James R.',  location: 'Fort Wayne', content: 'I appreciate their punctuality and care. They treat seniors with such respect and kindness. Highly recommended!',       rating: 5 },
              { id: 3, author_name: 'Linda W.',  location: 'Indianapolis',    content: 'Booking was easy, and the service was excellent. The wheelchair accessibility made transportation stress-free for my mom.', rating: 5 },
            ]).map(t => (
              <div key={t.id} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />)}
                </div>
                <p className="text-white/90 text-sm leading-relaxed mb-4 italic">"{t.content}"</p>
                <div>
                  <p className="font-semibold text-white">— {t.author_name}</p>
                  <p className="text-purple-200 text-xs">{t.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 9. Service areas ───────────────────────────────────────────── */}
      {areas.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex items-center justify-center gap-2 text-gray-600 text-sm mb-4 flex-wrap">
              <MapPin size={16} className="text-purple-700" />
              <span className="font-medium">Service Areas:</span>
              {areas.map((a, i) => <span key={a.id}>{a.name}{a.state ? `, ${a.state}` : ''}{i < areas.length - 1 ? ' · ' : ''}</span>)}
            </div>
          </div>
        </section>
      )}

      {/* ── 10. CTA ────────────────────────────────────────────────────── */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-4 font-heading">Ready for a Comfortable & Reliable Ride?</h2>
          <p className="text-gray-400 text-lg mb-8">Let MDS Transportation LLC handle your journey while you focus on your health.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book" className="btn-primary flex items-center gap-2 text-base">Book Your Ride Today <ArrowRight size={18} /></Link>
            <a href="tel:+13179821959" className="btn-outline border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white hover:border-gray-500 flex items-center gap-2 text-base">
              <Phone size={18} /> Call Us Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
